import apiClient, { TOKEN_KEY, REFRESH_KEY } from './apiClient';
import type {
  SessionUser, Membership, ActiveFees, Fee, Payment, Tenant, Role, AdminUser, ApplicationLogEntry,
  Sindicato,
} from '../../types/domain';

interface ApiOk<T> { success: true; }
type ApiPaged<TKey extends string, TItem> = { success: true; items: TItem[]; total: number; page: number; pageSize: number } | (Record<TKey, TItem[]> & { success: true });

// ─── Auth ────────────────────────────────────────────────────────────────────

export async function login(dni: string, password: string) {
  const { data } = await apiClient.post('/auth/login', { dni, password });
  if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
  if (data?.refreshToken) localStorage.setItem(REFRESH_KEY, data.refreshToken);
  return data as {
    success: true;
    token: string;
    refreshToken: string;
    user: SessionUser;
    memberships: Membership[];
    needsTenantSelection: boolean;
  };
}

/** Sin sesión: demo restablece a 123456; en real solo indica contactar al administrador. */
export async function forgotPassword(dni: string) {
  const { data } = await apiClient.post('/auth/forgot-password', { dni: dni.trim() });
  return data as { success: true; message: string };
}

export async function requestSignup(payload: { username: string; password: string; phone: string; email: string }) {
  const { data } = await apiClient.post('/auth/signup-request', payload);
  return data as { success: true; message: string; emailSent: boolean; simulated: boolean };
}

export async function logout() {
  try { await apiClient.post('/auth/logout'); } catch (_) { /* ignore */ }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export async function getMe() {
  const { data } = await apiClient.get('/auth/me');
  return data as { success: true; user: SessionUser; memberships: Membership[] };
}

export async function listMyMemberships() {
  const { data } = await apiClient.get('/auth/memberships');
  return data as { success: true; memberships: Membership[] };
}

// ─── Fees ────────────────────────────────────────────────────────────────────

export async function getActiveFees(params: { sindicatoId?: number | null } = {}) {
  const { data } = await apiClient.get('/fees/active', {
    params: {
      sindicatoId: params.sindicatoId != null && params.sindicatoId !== '' ? params.sindicatoId : undefined,
    },
  });
  return data as { success: true } & ActiveFees;
}

export async function listSindicatos(params: { tenantId?: number; includeInactive?: boolean } = {}) {
  const { data } = await apiClient.get('/sindicatos', {
    params: {
      tenantId: params.tenantId,
      includeInactive: params.includeInactive ? 'true' : undefined,
    },
  });
  return data as { success: true; sindicatos: Sindicato[] };
}

export async function createSindicato(payload: { tenantId?: number; code: string; name: string; description?: string; active?: boolean }) {
  const { data } = await apiClient.post('/sindicatos', payload);
  return data as { success: true; sindicato: Sindicato };
}

export async function updateSindicato(id: number, payload: Partial<Pick<Sindicato, 'code' | 'name' | 'description' | 'active'>>) {
  const { data } = await apiClient.put(`/sindicatos/${id}`, payload);
  return data as { success: true; sindicato: Sindicato };
}

export async function deleteSindicato(id: number) {
  const { data } = await apiClient.delete(`/sindicatos/${id}`);
  return data as { success: true; sindicato: Sindicato };
}

export async function listFees(params: { tenantId?: number | null; includeGlobal?: boolean; sindicatoId?: number | 'empresa' | null } = {}) {
  const { data } = await apiClient.get('/fees/admin/list', {
    params: {
      tenantId: params.tenantId ?? undefined,
      includeGlobal: params.includeGlobal === false ? 'false' : 'true',
      sindicatoId: params.sindicatoId === 'empresa' ? 'empresa' : (params.sindicatoId != null ? params.sindicatoId : undefined),
    },
  });
  return data as { success: true; fees: Fee[] };
}

export async function createFee(payload: Partial<Fee>) {
  const { data } = await apiClient.post('/fees/admin', payload);
  return data as { success: true; fee: Fee };
}

export async function updateFee(id: number, payload: Partial<Fee>) {
  const { data } = await apiClient.put(`/fees/admin/${id}`, payload);
  return data as { success: true; fee: Fee };
}

export async function deleteFee(id: number) {
  const { data } = await apiClient.delete(`/fees/admin/${id}`);
  return data as { success: true; message: string };
}

// ─── Payments ────────────────────────────────────────────────────────────────

export async function checkout(payload: {
  feeCode: 'AGREEMENT' | 'NO_AGREEMENT';
  documentNumber: string;
  notes?: string;
  sindicatoId?: number | null;
  forceStatus?: 'APPROVED' | 'PENDING' | 'REJECTED';
}) {
  const { data } = await apiClient.post('/payments/checkout', payload);
  return data as { success: true; payment: Payment };
}

export async function listPayments(params: Record<string, string | number | undefined> = {}) {
  const { data } = await apiClient.get('/payments/admin', { params });
  return data as { success: true; items: Payment[]; total: number; page: number; pageSize: number };
}

// ─── Admin Users ─────────────────────────────────────────────────────────────

export async function listUsers(params: { tenantId?: number } = {}) {
  const { data } = await apiClient.get('/admin/users', { params });
  return data as { success: true; users: AdminUser[] };
}

export async function listRoles() {
  const { data } = await apiClient.get('/admin/users/roles');
  return data as { success: true; roles: Role[] };
}

export async function createUser(payload: Partial<AdminUser> & { password?: string; tenantId?: number; roleId?: number }) {
  const { data } = await apiClient.post('/admin/users', payload);
  return data as { success: true; user: AdminUser };
}

export async function updateUser(id: number, payload: Partial<AdminUser> & { password?: string }) {
  const { data } = await apiClient.put(`/admin/users/${id}`, payload);
  return data as { success: true; user: AdminUser };
}

export async function deleteUser(id: number) {
  const { data } = await apiClient.delete(`/admin/users/${id}`);
  return data as { success: true; message: string };
}

export async function addUserMembership(userId: number, payload: { tenantId?: number; roleId: number }) {
  const { data } = await apiClient.post(`/admin/users/${userId}/memberships`, payload);
  return data as { success: true; membership: Membership };
}

export async function removeUserMembership(userId: number, membershipId: number) {
  const { data } = await apiClient.delete(`/admin/users/${userId}/memberships/${membershipId}`);
  return data as { success: true; message: string };
}

// ─── Admin Tenants ───────────────────────────────────────────────────────────

export async function listTenants(params: { includeInactive?: boolean } = {}) {
  const { data } = await apiClient.get('/admin/tenants', {
    params: { includeInactive: params.includeInactive ? 'true' : undefined },
  });
  return data as { success: true; tenants: Tenant[] };
}

export async function createTenant(payload: Partial<Tenant>) {
  const { data } = await apiClient.post('/admin/tenants', payload);
  return data as { success: true; tenant: Tenant };
}

export async function updateTenant(id: number, payload: Partial<Tenant>) {
  const { data } = await apiClient.put(`/admin/tenants/${id}`, payload);
  return data as { success: true; tenant: Tenant };
}

export async function deactivateTenant(id: number) {
  const { data } = await apiClient.delete(`/admin/tenants/${id}`);
  return data as { success: true; message: string };
}

// ─── Logs ────────────────────────────────────────────────────────────────────

export async function listLogs(params: Record<string, string | number | undefined> = {}) {
  const { data } = await apiClient.get('/admin/logs', { params });
  return data as { success: true; items: ApplicationLogEntry[]; total: number; page: number; pageSize: number };
}
